import { createContext } from 'react';
import { action, computed, observable, makeObservable } from 'mobx';
import { IGlobalModal } from 'Views/Components/Modal/GlobalModal';
import type { ClientsideConfiguration, BuildVersion, PasswordConfiguration } from 'Global';
import { IUserEntityAttributes } from 'Models/Entities/UserEntity';
import { measurementType } from './Enums';
import type { MeasurementRestrictionRules } from 'Util/_HumanWritten/MeasurementRestrictions';
import { CargoCharacteristicDto } from 'Modules/Cargo';
import { WidthDto } from './Entities/WidthEntity';
import { Permission } from 'Models/Permissions';

export interface IGroupResult {
	name: string;
	hasBackendAccess: boolean;
}
interface IUserResultBase {
	type: 'user-data';
	id: string;
	userName: string;
	email: string;
	groups: IGroupResult[];

	/**
	 * The capabilities the server resolved for this user, by name.
	 *
	 * Optional because it is only filled in on the single-user paths that produce a session payload.
	 * Absent is treated as "holds nothing" by {@link IStore.can}, which fails closed — a screen that
	 * cannot tell will hide the control rather than offer one the server would refuse.
	 */
	permissions?: Permission[];
}

export interface IUserResult extends IUserResultBase {
	user?: IUserEntityAttributes;
}

export type measurementDetails = {
	id: string;
	label: string;
	value: number;
	showInVehicleDropdown: boolean;
	showInTrailerDropdown: boolean;
	measurementType: measurementType;
} & MeasurementRestrictionRules;

export interface IStore {
	/**
	 * The current location in the application
	 */
	appLocation: 'frontend' | 'admin';

	/**
	 * The global modal that is stored in the app and can be called imperatively
	 */
	modal: IGlobalModal;

	/**
	 * This signifies weather we are logged in or not
	 * Only ever set this value to true if there is a value set in this.token
	 */
	readonly loggedIn: boolean;

	/**
	 * The user Id of the logged-in user
	 */
	readonly userId: string | undefined;

	/**
	 * The user name of the logged in user
	 */
	readonly userName: string | undefined;

	/**
	 * The email of the current logged in user
	 */
	readonly email: string | undefined;

	/**
	 * The groups that the logged in user are a part of
	 */
	readonly userGroups: IGroupResult[];

	/**
	 * Does this user have access to the backend admin views
	 */
	readonly hasBackendAccess: boolean;

	/**
	 * The configuration sent from the serverside
	 */
	readonly configuration: ClientsideConfiguration;

	/**
	 * The build version information of the application
	 */
	readonly buildVersion: BuildVersion;

	readonly passwordConfiguration: PasswordConfiguration;

	/**
	 * Is the frontend in edit mode
	 */
	frontendEditMode: boolean;

	/**
	 * Sets the current logged in user in the store
	 * @param userResult
	 */
	setLoggedInUser(userResult: IUserResult): void;

	/**
	 * Clears the logged in user data from the store
	 */
	clearLoggedInUser(): void;
	readonly userInitials: string | undefined;
	tempEmail: string;
	userData?: IUserEntityAttributes;
	readonly postcode: string | null;
	readonly phone: string | null;
	readonly lastName: string | null;
	readonly firstName: string | null;
	isSuper: boolean;
	isAdmin: boolean;
	isManager: boolean;
	isStaff: boolean;

	/**
	 * Whether the signed-in user holds a capability.
	 *
	 * Prefer this over the tier getters above wherever the question is "may they do this" rather than
	 * "how senior are they" — a capability can be granted to one person without moving their group,
	 * and a tier check cannot see that.
	 *
	 * For rendering only. The server checks the same capability again at the point the decision is
	 * made, so this deciding wrong costs a hidden button, never an unauthorised action.
	 */
	can(permission: Permission): boolean;

	/**
	 * Whether the signed-in user may create or change a booking at all. Customers always may (their
	 * own); staff need `BookOnBehalfOfCustomer`, even for a booking on their own account. Mirrors
	 * `BookingCapabilities.MayMakeBookings` on the server.
	 */
	mayMakeBookings: boolean;
	isVisitor: boolean;
	isInvoicedUser: boolean;
	isFixedPriceUser: boolean;
	lunioEnabled: boolean;

	// The fields are stored in the server as percentages, so they will need to be divided by 100 to arrive at the
	// correct multipliers
	// For example. The server stores the GST value as a percentage, so the server value is 10. The value we want for
	// the multiplier is a factor to multiply by, so the value here needs to be that percentage divided by 100
	// (10/100 = 0.1). This same logic applies for the credit card surcharge multiplier as well
	gstMultiplier: number;
	ccSurchargeMultiplier: number;
	measurements: measurementDetails[];
	widths: WidthDto[];
	getWidth: (id: string) => WidthDto | undefined;
	getWidthCapacityMultiplier: (id: string) => number;
	getBaseWidth(): WidthDto;

	cargoTypeCharacteristicsMap: Record<string, string[]>;
	towOnTypeCharacteristicsMap: Record<string, string[]>;
	cargoCharacteristics: CargoCharacteristicDto[];
	cargoCharacteristicMap: Record<string, CargoCharacteristicDto>;
	selectableCargoCharacteristics: CargoCharacteristicDto[];
	selectableTrailerCargoCharacteristics: CargoCharacteristicDto[];

	spinnerJSON: unknown;
	setSpinnerJSON(json: unknown): void;

	redirectedFromWaitlistEmail: boolean;
	setRedirectedFromWaitlistEmail(wasRedirectedFromEmail: boolean): void;

	hasServerVersionMismatch: boolean;
	mismatchedVersionCounter: number;
	versionPopUpTimestamp: Date | null;
}

/**
 * A global singleton store that contains a global state of data
 */
export class Store implements IStore {
	@observable
	user?: IUserResult;

	@observable
	appLocation: 'frontend' | 'admin' = 'frontend';

	@observable
	clientsideConfiguration: ClientsideConfiguration;

	@observable
	_buildVersion: BuildVersion;

	@observable
	_passwordConfiguration: PasswordConfiguration;

	modal: IGlobalModal;

	@observable
	useNewModal: boolean = false; // Default to old modal

	@action
	public setUseNewModal(useNew: boolean) {
		this.useNewModal = useNew;
	}

	@computed
	public get loggedIn() {
		return this.user !== undefined;
	}

	@computed
	public get userId(): string | undefined {
		return this.user ? this.user.id : undefined;
	}

	@computed
	public get userName(): string | undefined {
		return this.user?.userName;
	}

	@computed
	public get email(): string | undefined {
		return this.user ? this.user.email : undefined;
	}

	@computed
	public get userGroups(): IGroupResult[] {
		if (this.user) {
			return [...this.user.groups];
		}
		return [];
	}

	@computed
	public get hasBackendAccess() {
		if (this.user) {
			return this.user.groups.some(ug => ug.hasBackendAccess);
		}
		return false;
	}

	@computed
	public get configuration(): ClientsideConfiguration {
		return this.clientsideConfiguration;
	}

	@computed
	public get buildVersion(): BuildVersion {
		return this._buildVersion;
	}

	@computed
	get passwordConfiguration(): PasswordConfiguration {
		return this._passwordConfiguration;
	}

	@observable
	public frontendEditMode = false;

	@action
	public setLoggedInUser(userResult: IUserResult) {
		this.user = userResult;
	}

	@action
	public clearLoggedInUser() {
		this.user = undefined;
	}

	@action
	public setClientsideDataConfiguration(config: ClientsideConfiguration) {
		this.clientsideConfiguration = config;
	}

	@action
	public setBuildVersion(build: BuildVersion) {
		this._buildVersion = build;
	}

	@action
	public setPasswordConfiguration(passwordConfiguration: PasswordConfiguration) {
		this._passwordConfiguration = passwordConfiguration;
	}

	constructor() {
		makeObservable(this);
		this.configureUser();
		this.configureClientsideData();
		this.configureBuildVersion();
		this.configureMultipliers();
		this.configureMeasurements();
		this.configureCargoTypeCharacteristicsMap();
		this.configureLunio();
		this.configurePasswordConfiguration();
	}

	configureUser() {
		if (window.loginData && !this.user) {
			this.setLoggedInUser(window.loginData);
		}
	}

	configureClientsideData() {
		this.setClientsideDataConfiguration(window.clientsideDataConfiguration);
	}

	configureBuildVersion() {
		this.setBuildVersion(window.buildVersion);
	}

	configurePasswordConfiguration() {
		this.setPasswordConfiguration(window.passwordConfiguration);
	}

	configureMultipliers() {
		try {
			if (window.gstMultiplier) {
				this.setGstMultiplier(window.gstMultiplier);
			}
		} catch (e) {
			/* Suppress error parsing cookie */
		}

		try {
			if (window.ccSurchargeMultiplier) {
				this.setCcSurchargeMultiplier(window.ccSurchargeMultiplier);
			}
		} catch (e) {
			/* Suppress error parsing cookie */
		}
	}

	configureLunio() {
		this.setLunioEnabled(window.lunioEnabled ?? false);
	}

	configureMeasurements() {
		try {
			if (window.measurements) {
				this.measurements = window.measurements;
			}
			if (window.widths) {
				this.widths = window.widths;
			}
		} catch (e) {
			/* Suppress error parsing cookie */
		}
	}

	configureCargoTypeCharacteristicsMap() {
		try {
			if (window.cargoTypeCharacteristicsMap) {
				this.cargoTypeCharacteristicsMap = window.cargoTypeCharacteristicsMap;
			}
			if (window.towOnTypeCharacteristicsMap) {
				this.towOnTypeCharacteristicsMap = window.towOnTypeCharacteristicsMap;
			}
			if (window.cargoCharacteristics) {
				this.cargoCharacteristics = window.cargoCharacteristics;
				this.cargoCharacteristicMap = this.cargoCharacteristics.reduce(
					(record, item) => {
						record[item.id] = item;
						return record;
					},
					{} as Record<string, CargoCharacteristicDto>,
				);
			}
		} catch (e) {
			/* Suppress error parsing cookie */
		}
	}

	@observable
	hasServerVersionMismatch: boolean = false;

	@observable
	mismatchedVersionCounter: number = 0;

	@observable
	versionPopUpTimestamp: Date | null = null;

	@computed
	public get userInitials(): string | undefined {
		if (!this.user) {
			return undefined;
		}

		if (this.userData) {
			return `${this.userData.firstName[0]}${this.userData.lastName[0]}`.toUpperCase();
		}

		if (this.userName) {
			return this.userName.substring(0, 2).toUpperCase();
		}

		return 'ME'; // Default when we don't know what else to do!
	}

	@computed
	public get firstName(): string | null {
		return this.userData?.firstName ?? null;
	}

	@computed
	public get lastName(): string | null {
		return this.userData?.lastName ?? null;
	}

	@computed
	public get postcode(): string | null {
		return this.userData?.postcode ?? null;
	}

	@computed
	public get phone(): string | null {
		return this.userData?.phone ?? null;
	}

	tempEmail: string;

	@action
	public setTempEmail(tempEmail: string) {
		this.tempEmail = tempEmail;
	}

	@action
	public getTempEmail() {
		return this.tempEmail;
	}

	@computed
	get userData(): IUserEntityAttributes | undefined {
		return this.user?.user;
	}

	@computed
	get isInvoicedUser(): boolean {
		return store.userGroups.some(ug => ug.name === 'InvoicedUser');
	}

	@computed
	get isFixedPriceUser(): boolean {
		return store.userGroups.some(ug => ug.name === 'FixedPriceUser');
	}

	@computed
	get isVisitor(): boolean {
		return !store.userData;
	}

	@computed
	get isStaff(): boolean {
		return store.userGroups.some(
			ug =>
				ug.name === 'Staff' ||
				ug.name === 'Manager' ||
				ug.name === 'Admin' ||
				ug.name === 'Super Administrators',
		);
	}

	@computed
	get isManager(): boolean {
		return store.userGroups.some(
			ug => ug.name === 'Manager' || ug.name === 'Admin' || ug.name === 'Super Administrators',
		);
	}

	@computed
	get isAdmin(): boolean {
		return store.userGroups.some(ug => ug.name === 'Admin' || ug.name === 'Super Administrators');
	}

	@computed
	get isSuper(): boolean {
		return store.userGroups.some(ug => ug.name === 'Super Administrators');
	}

	/**
	 * A Set rather than `Array.includes` so a screen that asks about several capabilities in a render
	 * pass does not rescan the list each time. Recomputed only when the signed-in user changes.
	 */
	@computed
	private get permissionSet(): ReadonlySet<Permission> {
		return new Set(this.user?.permissions ?? []);
	}

	public can = (permission: Permission): boolean => this.permissionSet.has(permission);

	@computed
	get mayMakeBookings(): boolean {
		return !this.isStaff || this.can('BookOnBehalfOfCustomer');
	}

	@observable
	redirectedFromWaitlistEmail: boolean = false;

	@action
	public setRedirectedFromWaitlistEmail(wasRedirected: boolean) {
		this.redirectedFromWaitlistEmail = wasRedirected;
	}

	@observable
	gstMultiplier: number = 0;

	@action
	public setGstMultiplier(newMultiplier: number) {
		this.gstMultiplier = newMultiplier;
	}

	@observable
	ccSurchargeMultiplier: number = 0;

	@action
	public setCcSurchargeMultiplier(newMultiplier: number) {
		this.ccSurchargeMultiplier = newMultiplier;
	}

	@observable
	lunioEnabled: boolean = false;

	@action
	public setLunioEnabled(newValue: boolean) {
		this.lunioEnabled = newValue;
	}

	@observable
	spinnerJSON: unknown = '';

	@action
	public setSpinnerJSON(spinnerJSON: unknown) {
		this.spinnerJSON = spinnerJSON;
	}

	public measurements: measurementDetails[];

	public widths: WidthDto[];

	public getWidth(id: string): WidthDto | undefined {
		return this.widths.find(x => x.id === id);
	}

	public getWidthCapacityMultiplier(id: string): number {
		return this.widths.find(x => x.id === id)?.capacityMultiplier ?? 1;
	}

	public getBaseWidth(): WidthDto {
		return (
			this.widths.find(x => x.isBase) ?? {
				id: '',
				isBase: true,
				capacityMultiplier: 1,
				label: 'Default',
			}
		);
	}

	public cargoTypeCharacteristicsMap: { [cargoTypeId: string]: string[] } = {};
	public towOnTypeCharacteristicsMap: { [towOnTypeId: string]: string[] } = {};
	public cargoCharacteristics: CargoCharacteristicDto[];
	public cargoCharacteristicMap: Record<string, CargoCharacteristicDto>;

	public get selectableCargoCharacteristics() {
		return this.cargoCharacteristics.filter(x => x.isSelectableForVehicle);
	}

	public get selectableTrailerCargoCharacteristics() {
		return this.cargoCharacteristics.filter(x => x.isSelectableForTrailer);
	}
}

export const store: IStore = new Store();
export const StoreContext = createContext<IStore>(store);
